/**
 * AB#945 — "Design page" gives the page its OWN theme template instead of
 * converting the page in place: `templates/pages/<slug>.html` in the site's
 * theme, the page pointed at it via `template`, its content left byte-identical.
 * The template designer (AB#812) then edits that file.
 */
import { accountThemesApi, pagesApi, sitesApi, themesApi, type Page, type SiteTheme } from '@wordpuppi/shared';
import { confirmDialog } from '@/lib/dialog';

const CONTENT_BODY = `{% block content %}
<div class="page-custom">
    {% if page.show_title | default(value=true) %}<h1>{{ page.title }}</h1>{% endif %}
    {{ page.content | safe }}
</div>
{% endblock %}`;

/**
 * A full-width page template made from the theme's `wide.html`/`page.html`:
 * everything around `{% block content %}` is kept (title, canonical, head
 * blocks), the block itself becomes the page content without the narrow
 * `article.page` wrapper. The wrapper `<div>` is what makes the file designable
 * — a template with no element of its own is bounced by the canvas.
 */
export function pageTemplateSource(source: string | null | undefined): string {
  const fallback = `{% extends "base.html" %}\n\n${CONTENT_BODY}\n`;
  if (!source) return fallback;
  const tag = /\{%-?\s*(?:block\s+(\w+)|endblock\b[^%]*?)\s*-?%\}/g;
  let start = -1, depth = 0;
  for (const m of source.matchAll(tag)) {
    if (start < 0) {
      if (m[1] === 'content') { start = m.index; depth = 1; }
      continue;
    }
    depth += m[1] ? 1 : -1;
    if (depth === 0) return source.slice(0, start) + CONTENT_BODY + source.slice(m.index + m[0].length);
  }
  return fallback;
}

/** A page's template, `page.html` folded into the default (null). */
export const customTemplate = (t: string | null | undefined): string | null => (t && t !== 'page.html' ? t : null);

/** Mirror of the backend's `theme_display_name` ("coach-editorial" → "Coach Editorial"). */
const displayName = (id: string) => id.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export interface DesignTarget {
  to: '/sites/$siteSlug/theme/design';
  params: { siteSlug: string };
  search: { bp: string; path: string };
}

/**
 * Stock theme → fork it; a blueprint other sites (or the account/user) share →
 * copy it into a site blueprint and switch to it. Both ask first. Never writes
 * into either. Returns the theme the site now builds with + its `?bp=` key, or
 * null when the person declined.
 */
async function ownTheme(siteSlug: string, confirm: typeof confirmDialog): Promise<{ theme: SiteTheme; key: string } | null> {
  const theme = (await themesApi.get(siteSlug)).data;
  if (!theme) throw new Error('Could not load the site theme.');
  const ask = (from: string, name: string) =>
    confirm(`“${from}” can't be edited from this site. Design this page in a copy named “${name}”? This site will build with the copy.`, { title: 'Copy the theme' });

  if (theme.source === 'bundled' || theme.account_theme_id == null) {
    const from = displayName(theme.active_theme);
    if (!(await ask(from, `${from} — this site`))) return null;
    const forked = (await themesApi.fork(siteSlug, {})).data;
    if (!forked?.account_theme_id) throw new Error('The theme copy did not finish.');
    return { theme: forked, key: `site:${forked.account_theme_id}` };
  }

  const row = (await accountThemesApi.list(siteSlug)).data?.find((t) => t.id === theme.account_theme_id);
  // Same rule the server's site-file endpoints use for Baxter: only a blueprint
  // this site owns is safe to add page files to.
  // ponytail: a row this site can't list is treated as its own — the site
  // file endpoints write into it either way.
  if (!row || (row.scope === 'site' && row.in_use_by <= 1)) {
    return { theme, key: `${row?.scope ?? 'site'}:${theme.account_theme_id}` };
  }
  const name = `${row.name} — this site`;
  if (!(await ask(row.name, name))) return null;
  const copy = (await accountThemesApi.duplicate(row.id, { name, scope: 'site', site: siteSlug })).data;
  if (!copy) throw new Error('The theme copy did not finish.');
  await sitesApi.updateSettings(siteSlug, { account_theme_id: copy.id });
  const switched = (await themesApi.get(siteSlug)).data;
  if (!switched) throw new Error('Could not load the site theme.');
  return { theme: switched, key: `site:${copy.id}` };
}

/**
 * The whole "Design page" action. Returns where to navigate, or null if the
 * person cancelled the theme copy. Throws (ApiError message intact) on failure.
 * A page already on a custom template ("Edit design") opens that template;
 * otherwise it gets its own `pages/<slug>.html`.
 */
export async function designPage(
  siteSlug: string,
  page: Pick<Page, 'id' | 'slug' | 'template'>,
  confirm: typeof confirmDialog = confirmDialog,
): Promise<DesignTarget | null> {
  const current = customTemplate(page.template);
  if (!current && !page.slug) throw new Error('Give the page a slug before designing it.');
  const owned = await ownTheme(siteSlug, confirm);
  if (!owned) return null;

  const template = current ?? `pages/${page.slug}.html`;
  const path = `templates/${template}`;
  const has = (p: string) => owned.theme.files.some((f) => f.path === p);
  if (!has(path)) {
    const from = has('templates/wide.html') ? 'templates/wide.html' : has('templates/page.html') ? 'templates/page.html' : null;
    const source = from ? (await themesApi.getFile(siteSlug, from)).data?.content : null;
    await themesApi.putFile(siteSlug, path, pageTemplateSource(source), undefined, '');
  }
  // Metadata only: content/editor_mode are never sent, so the body is untouched.
  if (page.template !== template) await pagesApi.update(siteSlug, page.id, { template });
  return { to: '/sites/$siteSlug/theme/design', params: { siteSlug }, search: { bp: owned.key, path } };
}
